import { createContext, useContext } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { convertDbEmissionsToTransactions } from "@aclymatepackages/other-helpers";
import { buildCompanyAggregatedEmissionsArrays } from "@aclymatepackages/calcs/recurring";
import { subcategories } from "@aclymatepackages/subcategories";

import { useEmployeeStatuses } from "../components/employees";
import usePlatformLayoutContext from "./platformLayout";
import { AdminDataContext } from "./adminDataContext";

import {
  useCachedDisplayData,
  useAccountData,
  useCollectionDataListener
} from "../firebase";
import { useDisplayEvents } from "../components/events";
import { useTransactionsMonthsData } from "../components/emissions";
import { useFindCurrentEmployeeEmissions } from "../components/employees";
import { formatVendors } from "../components/vendors";
import useAccountingData from "../hooks/accountingData";

dayjs.extend(isSameOrAfter);

const convertAccountingYearsToEmissions = (accountingYears = []) =>
  accountingYears.flatMap(({ year, monthlySubcategoryEmissionsTons = [] }) =>
    monthlySubcategoryEmissionsTons.flatMap((subcategoriesObj, monthIndex) => {
      const date = dayjs()
        .year(year)
        .month(monthIndex)
        .startOf("month")
        .toDate();

      return Object.entries(subcategoriesObj).map(([subcategory, tonsCo2e]) => {
        const { scope } =
          subcategories.find(
            (subcategoryObj) => subcategoryObj.subcategory === subcategory
          ) || {};

        return {
          date,
          scope,
          subcategory,
          tonsCo2e,
          source: "accounting"
        };
      });
    })
  );

const EmissionsContext = createContext();

const calculateCurrentCarbonBalanceTons = ({ allEmissions, offsets = [] }) => {
  const offsetsTonsSum = offsets.length
    ? offsets.reduce(
        (sum, { tonsCo2e = 0 }) => Number(sum) + Number(tonsCo2e),
        0
      )
    : 0;

  const sumTransactions = () => {
    if (!allEmissions.length) {
      return 0;
    }

    const transactionsBeforeToday = allEmissions.filter(
      ({ emissionDate, date }) =>
        dayjs(emissionDate || date).isSameOrBefore(dayjs(), "day")
    );

    return transactionsBeforeToday.reduce(
      (sum, { tonsCo2e }) => sum + (tonsCo2e ? Number(tonsCo2e) : 0),
      0
    );
  };

  const emissionsSum = sumTransactions();
  const currentCarbonBalanceTons = offsetsTonsSum - emissionsSum;

  return { emissionsSum, offsetsTonsSum, currentCarbonBalanceTons };
};

export const EmissionsContextProvider = ({ children }) => {
  const { viewMode = "company" } = usePlatformLayoutContext() || {};
  const { adminData } = useContext(AdminDataContext) || {};

  const { companyData: adminCompanyData } = adminData || {};
  const { startDate: adminStartDate, employeeCount: adminEmployeeCount } =
    adminCompanyData || {};

  const [
    { earliestNewEmissionDate, accountingYears: closedAccountingYears = [] },
    accountingDateLoading
  ] = useAccountingData();
  const [offsets, offsetsLoading] = useCachedDisplayData("offsets");
  const [accountData, accountDataLoading] = useAccountData();
  const { startDate: companyStartDate, employeeCount: companyEmployeeCount } =
    accountData;

  const [utilitiesTransactions, utilitiesTransactionsLoading] =
    useCachedDisplayData("transactions", [
      ["subcategory", "in", ["gas", "electricity", "utilities"]]
    ]);
  const [dbEmissions, dbEmissionsLoading] = useCachedDisplayData("emissions");
  const [dbEmployees, dbEmployeesLoading] = useEmployeeStatuses();
  const [offices, officesLoading] = useCachedDisplayData("offices");
  const [events, eventsLoading] = useDisplayEvents();
  const [transactionsMonthsData, transactionsMonthsDataLoading] =
    useTransactionsMonthsData();

  const [matchedVendorTransactions, matchedVendorTransactionsLoading] =
    useCollectionDataListener("matched-vendors-aggregated-transactions");
  const [vendorsWithRelationships, vendorsWithRelationshipsLoading] =
    useCachedDisplayData("vendor-relationships");

  const findCurrentEmployeeEmissions = useFindCurrentEmployeeEmissions();

  const recurringEmissionsLoading =
    accountingDateLoading ||
    offsetsLoading ||
    dbEmployeesLoading ||
    officesLoading ||
    accountDataLoading ||
    utilitiesTransactionsLoading ||
    eventsLoading ||
    dbEmissionsLoading ||
    transactionsMonthsDataLoading;

  const employeesLoading =
    recurringEmissionsLoading || accountDataLoading || dbEmployeesLoading;

  const vendorsLoading =
    vendorsWithRelationshipsLoading ||
    recurringEmissionsLoading ||
    matchedVendorTransactionsLoading;

  const emissionStartDates = {
    admin: adminStartDate,
    company: earliestNewEmissionDate || companyStartDate
  };

  const employeeCount =
    viewMode === "admin" ? adminEmployeeCount : companyEmployeeCount;

  const emissionsStartDate = emissionStartDates[viewMode];

  const buildEmissionsArraysFromExternalData = () => {
    if (recurringEmissionsLoading) {
      return {};
    }

    const { allEmissions, ...otherEmissionsArrays } =
      buildCompanyAggregatedEmissionsArrays({
        companyData: {
          ...accountData,
          startDate: emissionsStartDate
        },
        dbEmissions,
        employees: dbEmployees,
        offices,
        events,
        utilitiesTransactions
      });

    const dbEmissionsAsTransactions =
      convertDbEmissionsToTransactions(dbEmissions);

    const combinedEmissions = [
      ...allEmissions,
      ...dbEmissionsAsTransactions,
      ...transactionsMonthsData
    ];

    const closedYears = new Set(closedAccountingYears.map(({ year }) => year));

    const hasClosedYears = closedYears.size > 0;

    const emissionsWithAccountingData = hasClosedYears
      ? [
          ...combinedEmissions.filter(
            ({ date, emissionDate }) =>
              !closedYears.has(dayjs(emissionDate || date).year())
          ),
          ...convertAccountingYearsToEmissions(closedAccountingYears)
        ]
      : combinedEmissions;

    return {
      ...otherEmissionsArrays,
      allEmissions: emissionsWithAccountingData
    };
  };

  const {
    emissionsMonths = [],
    monthlyCommuteEmissionsTons = [],
    monthlyUtilitiesEmissionsTons: rawMonthlyUtilitiesEmissionsTons = [],
    totalRecurringEmissionsTons = [],
    averageMonthlyRecurringEmissionsTons = [],
    separateUtilitiesOfficesDefaultEmissions = [],
    allEmissions = []
  } = buildEmissionsArraysFromExternalData();

  const monthlyUtilitiesEmissionsTons =
    rawMonthlyUtilitiesEmissionsTons.flatMap((item) =>
      Array.isArray(item) ? item : [item]
    );

  const filteredOffsets = offsets?.length
    ? offsets.filter(({ status }) => status !== "failed")
    : [];

  const { currentCarbonBalanceTons, emissionsSum } =
    calculateCurrentCarbonBalanceTons({
      allEmissions,
      offsets: filteredOffsets
    });

  const formatDbEmployees = () => {
    if (employeesLoading) {
      return [];
    }

    return findCurrentEmployeeEmissions({
      employees: dbEmployees,
      employeeCount,
      emissionsMonths
    });
  };

  const formatDbVendors = () => {
    if (vendorsLoading) {
      return [];
    }

    return formatVendors({
      vendorsWithRelationships,
      matchedVendorTransactions,
      transactionsMonthsData,
      emissionsSum
    });
  };

  const employees = formatDbEmployees();

  const vendors = formatDbVendors();

  return (
    <EmissionsContext.Provider
      value={{
        emissionsMonths,
        monthlyCommuteEmissionsTons,
        monthlyUtilitiesEmissionsTons,
        totalRecurringEmissionsTons,
        averageMonthlyRecurringEmissionsTons,
        emissionsLoading: recurringEmissionsLoading,
        separateUtilitiesOfficesDefaultEmissions,
        allEmissions,
        utilitiesTransactions,
        employees,
        vendors,
        currentCarbonBalanceTons
      }}
    >
      {children}
    </EmissionsContext.Provider>
  );
};

const useEmissionsContext = () => useContext(EmissionsContext);
export default useEmissionsContext;
